// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Users, Mail, Calendar, MapPin, DollarSign, Plus, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';
import { toast } from 'sonner';
import ManualRegistrationForm from '@/components/org-admin/ManualRegistrationForm';
import ManualCoachInvitationForm from '@/components/org-admin/ManualCoachInvitationForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EventDetails {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  venue_name: string;
  venue_address: string;
  venue_city: string;
  venue_state: string;
  venue_zip: string;
  registration_type: string;
  organization_id: string;
}

interface Registration {
  id: string;
  profile_id: string;
  registration_type: string;
  payment_status: string;
  total_amount: number;
  created_at: string;
  profiles: {
    name: string;
    email: string;
  };
}

interface CoachInvitation {
  id: string;
  coach_email: string;
  coach_name: string;
  status: string;
  invited_at: string;
}

const OrgAdminEventDetails: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [coachInvitations, setCoachInvitations] = useState<CoachInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showCoachInviteForm, setShowCoachInviteForm] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.id && eventId) {
      fetchOrganizationAndEventData();
    }
  }, [profile?.id, eventId]);

  const fetchOrganizationAndEventData = async () => {
    if (!profile?.id || !eventId) return;

    try {
      setLoading(true);

      // Get organization_id from organization_members (RLevents schema)
      const { data: roleData, error: roleError } = await (supabase as any)
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', profile.id)
        .eq('status', 'active')
        .in('role', ['org_admin', 'org_staff'])
        .maybeSingle();

      if (roleError || !roleData?.organization_id) {
        toast.error('Failed to load organization data');
        setLoading(false);
        return;
      }

      setOrganizationId(roleData.organization_id);

      // Fetch event details
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .eq('organization_id', roleData.organization_id)
        .single();

      if (eventError) {
        toast.error('Event not found or access denied');
        navigate('/org-admin-dashboard');
        return;
      }

      setEvent(eventData);

      // Fetch registrations
      const { data: registrationsData, error: registrationsError } = await supabase
        .from('event_registrations')
        .select(`
          id,
          profile_id,
          registration_type,
          payment_status,
          total_amount,
          created_at,
          profiles!inner (
            name,
            email
          )
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (registrationsError) throw registrationsError;
      
      // Transform the data to match our interface
      const transformedRegistrations = (registrationsData || []).map((reg: any) => ({
        ...reg,
        profiles: Array.isArray(reg.profiles) ? reg.profiles[0] : reg.profiles
      }));
      
      setRegistrations(transformedRegistrations);

      // Fetch coach invitations
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('coach_invitations')
        .select('*')
        .eq('event_id', eventId)
        .order('invited_at', { ascending: false });

      if (invitationsError) throw invitationsError;
      setCoachInvitations(invitationsData || []);

    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error('Failed to load event data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseLocalDate(dateString), 'MMM d, yyyy');
    } catch {
      return 'Date TBD';
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(parseLocalDate(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Date TBD';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      paid: { label: 'Paid', className: 'bg-green-100 text-green-800' },
      accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800' },
      declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
    };
    
    const badge = badges[status as keyof typeof badges] || { label: status, className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const handleRegistrationSuccess = () => {
    setShowRegistrationForm(false);
    fetchOrganizationAndEventData();
    toast.success('Registration created successfully');
  };

  const handleCoachInviteSuccess = () => {
    setShowCoachInviteForm(false);
    fetchOrganizationAndEventData();
    toast.success('Coach invitation sent successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading event details...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-4 bg-destructive/10 border-l-4 border-destructive text-destructive">
        <p className="font-bold">Event Not Found</p>
        <p>The requested event could not be found or you don't have access to it.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/org-admin-dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      {/* Event Details Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">{event.name}</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(event.status)}
                <span className="text-sm text-muted-foreground capitalize">
                  {event.registration_type} Registration
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{event.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Event Dates</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(event.start_date)} - {formatDate(event.end_date)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Venue</p>
                <p className="text-sm text-muted-foreground">
                  {event.venue_name}<br />
                  {event.venue_city}, {event.venue_state} {event.venue_zip}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={() => setShowRegistrationForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Registration
        </Button>
        <Button onClick={() => setShowCoachInviteForm(true)} variant="outline" className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Invite Coach
        </Button>
      </div>

      {/* Registrations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Registrations ({registrations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {registrations.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No registrations yet</p>
          ) : (
            <div className="space-y-3">
              {registrations.map((reg) => (
                <div key={reg.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{reg.profiles?.name || 'Unknown'}</p>
                      <p className="text-sm text-muted-foreground">{reg.profiles?.email}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2">
                        <span className="capitalize">{reg.registration_type}</span>
                        <span>{formatDateTime(reg.created_at)}</span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {formatCurrency(reg.total_amount)}
                        </span>
                      </div>
                    </div>
                    <div>
                      {getStatusBadge(reg.payment_status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coach Invitations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Coach Invitations ({coachInvitations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {coachInvitations.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No coach invitations sent</p>
          ) : (
            <div className="space-y-3">
              {coachInvitations.map((invitation) => (
                <div key={invitation.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{invitation.coach_name}</p>
                      <p className="text-sm text-muted-foreground">{invitation.coach_email}</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Invited: {formatDateTime(invitation.invited_at)}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(invitation.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Registration Dialog */}
      <Dialog open={showRegistrationForm} onOpenChange={setShowRegistrationForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Manual Registration</DialogTitle>
          </DialogHeader>
          {organizationId && (
            <ManualRegistrationForm
              eventId={eventId!}
              organizationId={organizationId}
              onSuccess={handleRegistrationSuccess}
              onCancel={() => setShowRegistrationForm(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Coach Invitation Dialog */}
      <Dialog open={showCoachInviteForm} onOpenChange={setShowCoachInviteForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Invite Coach</DialogTitle>
          </DialogHeader>
          {organizationId && (
            <ManualCoachInvitationForm
              eventId={eventId!}
              eventName={event.name}
              organizationId={organizationId}
              onSuccess={handleCoachInviteSuccess}
              onCancel={() => setShowCoachInviteForm(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgAdminEventDetails;